
import { computed, defineComponent, onMounted, PropType, reactive, ref, toRaw, watch } from 'vue'

// components
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheTextarea from '@/common/components/UI/TheTextarea/index.vue'
import RadioGroup from '@/common/components/UI/RadioGroup/index.vue'

// constants
import {
    SHIPMENT_TYPES,
    CITY_SHIPMENT_TYPES,
    PAYMENT_METHOD_OPTIONS_INTRACITY,
} from '@/core/constants/create_order.constants'

// validation
import useVuelidate from '@vuelidate/core'

// types
import { CompletedCargoFTLBidT } from '@/core/types/create_order.types'

// composable
import { decimal, integer, requiredIf, minValue, required } from '@vuelidate/validators'

export default defineComponent({
    components: {
        'the-input': TheInput,
        'the-button': TheButton,
        'the-textarea': TheTextarea,
        'radio-group': RadioGroup,
    },
    props: {
        stepNumber: {
            type: Number,
            default: 0,
        },
        ftlType: {
            type: Number,
            default: 0,
        },
        opened: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isCompleted: {
            type: Boolean,
            default: false,
        },
        updatingCargo: {
            type: Object as PropType<CompletedCargoFTLBidT>,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['submit', 'cancel-update', 'start-update-cargo'],
    setup(props, { emit }) {
        const state = reactive({
            weight: null as number | null,
            places: null as number | null,
            depth: null as number | null,
            width: null as number | null,
            height: null as number | null,
            //
            special_conditions: null as string | null,
            //
            dop_order_number: null as string | null,
            shipment_type: 1,
            payment_method: 3,
        })

        const CargoValidationFTLBid = {
            weight: {
                required,
                decimal,
                minValue: minValue(0),
            },
            places: {
                required: requiredIf(() => props.ftlType === 1),
                integer,
                minValue: minValue(0),
            },
            depth: {
                required,
                decimal,
                minValue: minValue(0),
            },
            width: {
                required,
                decimal,
                minValue: minValue(0),
            },
            height: {
                required,
                decimal,
                minValue: minValue(0),
            },
        }

        const state_form$ = useVuelidate(CargoValidationFTLBid, state)

        const is_loading = ref(false)

        const resetForm = () => {
            state.weight = null
            state.places = null
            state.depth = null
            state.width = null
            state.height = null
            state.special_conditions = null
            state.dop_order_number = null
            state.shipment_type = 1
            state.payment_method = 3

            state_form$.value.$reset()
        }

        const emitSubmit = async () => {
            if (is_loading.value) {
                return
            }
            state_form$.value.$touch()
            if (state_form$.value.$invalid || props.disabled) return

            if (props.isUpdate) {
                emit('submit', {
                    ...state,
                    weight: Number(state.weight),
                    places: Number(state.places),
                    depth: Number(state.depth),
                    width: Number(state.width),
                    height: Number(state.height),
                })

                resetForm()
                return
            }

            emit('submit', {
                ...state,
                weight: state.weight && Number(state.weight),
                places: state.places && Number(state.places),
                depth: state.depth && Number(state.depth),
                width: state.width && Number(state.width),
                height: state.height && Number(state.height),
            })

            resetForm()
        }

        const emitUpdateCargo = () => {
            emit('start-update-cargo')
        }

        const emitCancelUpdate = () => {
            resetForm()
            emit('cancel-update')
        }

        watch(
            () => props.updatingCargo,
            (newest) => {
                if (!newest) return
                const cargo = toRaw(newest.logistics_info)

                state.dop_order_number = cargo.dop_order_number
                state.weight = cargo.weight
                state.places = cargo.places
                state.depth = cargo.depth
                state.width = cargo.width
                state.height = cargo.height
                if (cargo.special_conditions) state.special_conditions = cargo.special_conditions
                state.shipment_type = cargo.shipment_type
                state.payment_method = cargo.payment_method
            }
        )

        onMounted(() => {
            if (props.isUpdate && props.updatingCargo) {
                const cargo = props.updatingCargo.logistics_info

                state.dop_order_number = cargo.dop_order_number
                state.weight = cargo.weight
                state.places = cargo.places
                state.depth = cargo.depth
                state.width = cargo.width
                state.height = cargo.height
                if (cargo.special_conditions) state.special_conditions = cargo.special_conditions
                state.shipment_type = cargo.shipment_type
                state.payment_method = cargo.payment_method
            }
        })

        const shipmentTypes = computed(() => {
            return props.ftlType === 2 ? CITY_SHIPMENT_TYPES : SHIPMENT_TYPES
        })

        return {
            shipmentTypes,
            PAYMENT_METHOD_OPTIONS_INTRACITY,
            state,
            state_form$,
            emitSubmit,
            emitCancelUpdate,
            emitUpdateCargo,
            is_loading,
        }
    },
})
